import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { Card, CardContent, Division, Flex, Heading, Skeleton } from "@ir/ui";
import { memo, useMemo } from "react";
import { PromoProgramEditMode } from "@/store/promotion";
import { usePromotionEdit } from "../hooks";
import { ProgramDetailsCard } from "./ProgramDetailsCard";
import { ProgramLifecycleCard } from "./ProgramLifecycleCard";

interface ProgramInfoTabProps {
  promoDetail: PromoDetail | null;
  dateFormat: string;
  numberFormat: string;
  isLoading: boolean;
}

const SkeletonRow = memo(({ children }: { children: React.ReactNode }) => {
  return (
    <Flex justifyContent="space-between" className="py-2 px-4">
      {children}
    </Flex>
  );
});

const ProgramInfoCardSkeleton = memo(({ title }: { title: string }) => {
  return (
    <Card className="bg-white mb-3">
      <CardContent className="p-0">
        <Division className="px-4 py-3 mb-2">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            {title}
          </Heading>
        </Division>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "25%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "10%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "35%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "12%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "35%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "12%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "25%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "11%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "25%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "13%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "28%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "10%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "25%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "11%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "25%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "13%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "28%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "10%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "28%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "10%" }} />
        </SkeletonRow>
        <SkeletonRow>
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "28%" }} />
          <Skeleton height={8} variant="rounded" animation="wave" style={{ width: "20%" }} />
        </SkeletonRow>
      </CardContent>
    </Card>
  );
});

ProgramInfoCardSkeleton.displayName = "ProgramInfoCardSkeleton";

const ProgramInfoTab = memo(({ promoDetail, dateFormat, numberFormat, isLoading }: ProgramInfoTabProps) => {
  const { editMode } = usePromotionEdit();
  const isEditingDetails = useMemo(() => editMode === PromoProgramEditMode.DETAILS, [editMode]);

  if (isLoading || !promoDetail) {
    return (
      <Division>
        <ProgramInfoCardSkeleton title="Promotion Details" />
        <ProgramInfoCardSkeleton title="Lifecycle" />
      </Division>
    );
  }

  return (
    <Division className="program-info-tab">
      <ProgramDetailsCard promoDetail={promoDetail} dateFormat={dateFormat} numberFormat={numberFormat} isEditingDetails={isEditingDetails} />
      <ProgramLifecycleCard promoDetail={promoDetail} dateFormat={dateFormat} />
    </Division>
  );
});

ProgramInfoTab.displayName = "ProgramInfoTab";

export default ProgramInfoTab;
